<template>
    <div class="rating-mode" :class="{ 'is-disabled': disabled }">
        <div class="rating-mode__header">
            <span class="rating-mode__title">{{ $t('RatingStyle') }}</span>
            <div class="rating-mode__segment" role="radiogroup" :aria-label="$t('RatingStyle')">
                <button v-for="mode in modes"
                        :key="mode.value"
                        type="button"
                        class="rating-mode__choice"
                        :class="{ 'is-active': current === mode.value }"
                        role="radio"
                        :aria-checked="current === mode.value ? 'true' : 'false'"
                        :disabled="disabled"
                        @click="choose(mode.value)">
                    <v-icon small class="rating-mode__icon">{{ mode.icon }}</v-icon>
                    <span>{{ $t(mode.label) }}</span>
                </button>
            </div>
        </div>

        <div class="rating-mode__preview">
            <span class="rating-mode__caption">{{ $t('Preview') }}</span>
            <div class="rating-mode__stage">
                <StarRating v-if="current === 'Star'" compact :length="rateLength"></StarRating>
                <SmileyRating v-else compact></SmileyRating>
            </div>
        </div>
    </div>
</template>

<script>
    import SmileyRating from '@/components/controls/SmileyRating.vue'
    import StarRating from '@/components/controls/StarRating.vue'

    /*
     * Builder-side picker for the Rating question's display mode.
     * v-model holds the stored questionTypeParams string: 'Star' or 'Emoji'.
     */
    export default {
        name: 'RatingModePicker',
        components: { SmileyRating, StarRating },
        props: {
            value: { type: String, default: 'Star' },
            disabled: { type: Boolean, default: false },
            rateLength: { type: Number, default: 5 }
        },
        data() {
            return {
                modes: [
                    { value: 'Star', label: 'StarRating', icon: 'mdi-star' },
                    { value: 'Emoji', label: 'EmojiRating', icon: 'mdi-emoticon-happy-outline' }
                ]
            }
        },
        computed: {
            current() {
                return this.value === 'Emoji' ? 'Emoji' : 'Star'
            }
        },
        methods: {
            choose(mode) {
                if (this.disabled || mode === this.current) return
                this.$emit('input', mode)
                this.$emit('change', mode)
            }
        }
    }
</script>

<style scoped>
    .rating-mode {
        --rm-surface: #f3f6f5;
        --rm-card: #ffffff;
        --rm-text: #1f2a28;
        --rm-muted: #6b7a77;
        --rm-primary: #0a716e;
        --rm-ease: cubic-bezier(.2, .8, .2, 1);
        display: flex;
        flex-direction: column;
        gap: 14px;
        width: 100%;
        max-width: 640px;
        margin-top: 8px;
    }

    .rating-mode__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 10px;
    }

    .rating-mode__title {
        font-size: 14px;
        font-weight: 700;
        color: var(--rm-text);
    }

    .rating-mode__segment {
        display: inline-flex;
        padding: 4px;
        border-radius: 999px;
        background: var(--rm-surface);
        gap: 2px;
    }

    .rating-mode__choice {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        min-height: 36px;
        padding: 0 16px;
        border: 0;
        border-radius: 999px;
        background: transparent;
        color: var(--rm-muted);
        font: inherit;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color .2s var(--rm-ease), color .2s var(--rm-ease), box-shadow .2s var(--rm-ease);
    }

    .rating-mode__choice .rating-mode__icon {
        color: inherit;
        transition: color .2s var(--rm-ease);
    }

    .rating-mode__choice:hover { color: var(--rm-text); }
    .rating-mode__choice:focus { outline: none; }
    .rating-mode__choice:focus-visible { box-shadow: 0 0 0 2px var(--rm-card), 0 0 0 4px var(--rm-primary); }

    .rating-mode__choice.is-active {
        background: var(--rm-card);
        color: var(--rm-primary);
        box-shadow: 0 1px 2px rgba(15, 23, 42, .06), 0 4px 10px -6px rgba(15, 23, 42, .25);
    }

    .rating-mode__preview {
        position: relative;
        padding: 20px 16px 16px;
        border-radius: 18px;
        border: 1px dashed #cfd9d6;
        background: var(--rm-card);
    }

    .rating-mode__caption {
        position: absolute;
        top: -10px;
        inset-inline-start: 16px;
        padding: 0 8px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .02em;
        color: var(--rm-muted);
        background: var(--rm-card);
    }

    .rating-mode__stage {
        display: flex;
        justify-content: center;
    }

    .rating-mode.is-disabled .rating-mode__choice { cursor: default; }
    .rating-mode.is-disabled .rating-mode__choice:not(.is-active) { opacity: .5; }

    @media (prefers-reduced-motion: reduce) {
        .rating-mode__choice, .rating-mode__choice .rating-mode__icon { transition: none; }
    }
</style>
